import React from 'react';
import { NumericInput as BPNumericInput } from '@blueprintjs/core';
import { useAppIntlContext } from '../AppIntlProvider';
import { fmtNumber } from '@/utils/format';

interface LocaleNumericInputProps {
  /**
   * Force the displayed value to render with this many decimal digits, even
   * for integer or trailing-zero values. Without this, Blueprint's
   * `toLocaleString`-based display silently strips decimals (e.g. `2513.0`
   * becomes `"2 513"`), which is misleading for money amounts where the user
   * expects `"2 513,00"`. The raw numeric value still flows through
   * `onValueChange` unchanged.
   */
  fractionDigits?: number;
  [key: string]: any;
}

/**
 * Drop-in replacement for `@blueprintjs/core`'s NumericInput that makes
 * decimals actually typable in a controlled, number-valued field:
 *
 *  1. Defaults the `locale` prop from the active intl locale — without this,
 *     FR users who type "10,4484" get NaN (Blueprint's default parser only
 *     accepts "." as decimal separator).
 *  2. Defaults `allowNumericCharactersOnly` to `false` so the decimal point
 *     (`.`) stays typeable under a FR locale — Blueprint's char filter
 *     otherwise only permits the locale separator (the comma in FR) and
 *     silently swallows the `.` keystroke. Both `.` and `,` now reach
 *     Blueprint's parser, which accepts either.
 *  3. Owns the displayed text string locally instead of letting Blueprint
 *     derive it from the numeric `value` prop. Blueprint's
 *     `getDerivedStateFromProps` rewrites its input text to
 *     `value.toString()` on EVERY render, so when the parent stores the
 *     parsed number (the usual Formik pattern), a half-typed decimal like
 *     "3." round-trips parent → number `3` → "3" before the fractional
 *     digits can be typed. Holding the raw string here lets "3.", "3,",
 *     "3.5" survive; the parsed number still flows out through
 *     `onValueChange` unchanged.
 *  4. Re-syncs the text from the `value` prop ONLY while the input is not
 *     focused. This keeps two consumer patterns working at once: fields that
 *     commit on every keystroke (Formik) and fields that commit on blur
 *     (`asyncControl` table cells / grids, where the prop deliberately lags
 *     behind the typed value). Without the focus guard, a lagging prop would
 *     be mistaken for an external change and wipe the user's input.
 *  5. Optionally enforces a fixed number of fraction digits on display via
 *     `fractionDigits`, so money fields show "2 513,00" instead of "2 513".
 *
 * Callers can still pass `allowNumericCharactersOnly` to opt back in. An
 * uncontrolled field (no `value` prop) is passed straight through.
 */
export const LocaleNumericInput = React.forwardRef(function LocaleNumericInput(
  {
    fractionDigits,
    value,
    onValueChange,
    onFocus,
    onBlur,
    ...rest
  }: LocaleNumericInputProps,
  ref: any,
) {
  // Pull from React context so the locale is correct on first paint —
  // `intl.getInitOptions()` can return an empty object before init resolves,
  // which causes a visible flash of unformatted numbers.
  // La locale ne sert plus qu'à l'ANALYSE de la saisie par Blueprint : elle est
  // épinglée sur `fr-FR` — la convention unique du produit (virgule décimale) —
  // pour que ce que Blueprint relit corresponde à ce que le champ affiche.
  // Elle était auparavant dérivée de la locale de l'application/du navigateur :
  // un poste anglais affichait « 1,234.56 » là où toutes les grilles montrent
  // « 1 234,56 ». Un appelant peut toujours l'imposer via la prop `locale`.
  useAppIntlContext();
  const locale = rest.locale ?? 'fr-FR';

  const controlled = value !== undefined;

  // Render an external numeric value in the product's single convention
  // (espace insécable + virgule), keeping thousands grouping and, when
  // `fractionDigits` is set, trailing zeros for money amounts.
  // `v.toLocaleString(locale, …)` dépendait de la locale du navigateur : le
  // même champ affichait « 1,234.56 » ou « 1 234,56 » selon le poste.
  const toText = React.useCallback(
    (v: any) => {
      if (v == null || v === '') return '';
      if (typeof v !== 'number') return String(v);
      if (Number.isNaN(v)) return '';
      if (fractionDigits != null) return fmtNumber(v, fractionDigits);
      // Sans `fractionDigits`, on n'impose AUCUNE décimale : on rend celles que
      // la valeur porte réellement (« 3 » reste « 3 », « 10,4484 » reste entier).
      const s = String(v);
      const decimals =
        s.includes('e') || s.includes('E') ? 0 : (s.split('.')[1] || '').length;
      return fmtNumber(v, Math.min(decimals, 20));
    },
    [fractionDigits],
  );

  const [text, setText] = React.useState(() => toText(value));
  const [focused, setFocused] = React.useState(false);
  // What we last emitted upstream, so a genuine external change can be told
  // apart from the echo of our own onValueChange.
  const lastEmitted = React.useRef(value);

  // Re-sync the text from the prop only on a real external change (form reset,
  // switching records, programmatic set) AND only while not actively typing.
  React.useEffect(() => {
    if (!focused && value !== lastEmitted.current) {
      lastEmitted.current = value;
      setText(toText(value));
    }
  }, [value, focused, toText]);

  const handleValueChange = (
    num: number,
    str: string,
    el: HTMLInputElement | null,
  ) => {
    lastEmitted.current = num;
    setText(str);
    onValueChange?.(num, str, el);
  };

  const handleFocus = (e: any) => {
    setFocused(true);
    onFocus?.(e);
  };

  const handleBlur = (e: any) => {
    setFocused(false);
    onBlur?.(e);
  };

  return (
    <BPNumericInput
      allowNumericCharactersOnly={false}
      {...rest}
      {...(controlled ? { value: text } : {})}
      locale={locale}
      onValueChange={handleValueChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      ref={ref}
    />
  );
});
